import { useContext, useEffect, useRef, useState } from "react";
import { Localization, SessionContext } from "@viamap/viamap2-common";
import { Spinner } from "react-bootstrap";
import { ADModalBody, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { closeWindow, minimizeOpenWindowsForStedfaestelse, openWindow, WindowId, type ManagedWindow } from "src/WindowManager/WindowState";
import { isWindowOpen, useWindowContext } from "src/WindowManager/useWindowContext";
import { ApplicationStateContext } from "src/states/ApplicationState";
import { canUsePhaseA, canUsePhaseB } from "src/components/ServitutRedegoerelseAccess";
import { getPdfPageCount } from "src/modules/ANNModule/ANNPDFCutter";
import {
   buildConsecutivePages,
   clampPagesToDocument,
   getViewerInitialAktPages,
   mapAktPagesToCutSelection,
   normalizePages,
} from "src/modules/ANNModule/ANNServitutDocumentAnnotations";
import { AnnotationContextType, type ANNAnnotationRecord } from "src/modules/ANNModule/ANNTypes";
import { useANNContext, useDeleteAnnotation, useSaveAnnotation } from "src/modules/ANNModule/ANNState";
import { ANNEditorActionType, useANNEditorContext } from "src/modules/ANNModule/ANNEditorState";
import type { SFEColorEntry } from "src/components/ServitutRapportData";
import { message } from "src/components/Message";
import { ServitutReportProvider, useServitutReport } from "../store/context";
import { ServitutReportActionType } from "../store/actions";
import { useServitutReportEffects } from "../store/effects";
import { selectIncompleteARows, selectRejectedARows, selectRow, selectRowAktId, selectWorkingDocumentLabel } from "../store/selectors";
import { canonicalDocumentId, rowDocumentId } from "../model/identity";
import { isDigitalRow, isNativeDigitalDocument } from "../model/row";
import type { ServitutRedegoerelsePhase } from "../model/report";
import { getCutPdfForContent, getRowSourcePdfBlob, rowTingbogDokumentId, withRowPaategningPages } from "../adapters/rowIo";
import { toViewerAnalysisResult } from "../adapters/viewPayload";
import { loadLastCaseDescription, loadReportSkeleton, saveLastCaseDescription, saveReportSkeleton, clearStalePreparedCache } from "../adapters/session";
import { confirmForeignAnnotationDelete } from "../adapters/confirmForeignAnnotation";
import { ServitutMatrix } from "./ServitutMatrix";
import "src/components/ServitutRedegoerelseRapport.css";

export type ServitutRedegoerelseWindowParams = {
   prepareColorList?: SFEColorEntry[];
   initialPhase?: ServitutRedegoerelsePhase;
   datoLoebenrFilter?: string;
};

function BootProgress(props: {
   progress: { stage: "registry" | "easements"; completed: number; total: number } | null;
   error?: string;
}) {
   if (props.error) {
      return (
         <div className="servitut-redegoerelse servitut-redegoerelse--boot">
            <div className="servitut-redegoerelse__boot" role="alert">
               <div className="servitut-redegoerelse__bootTitle">{props.error}</div>
            </div>
         </div>
      );
   }
   const progress = props.progress;
   const indeterminate = !progress || progress.total < 1 || (progress.stage === "registry" && progress.completed < 1);
   const percent = progress && progress.total > 0 ? Math.min(100, Math.round((progress.completed / progress.total) * 100)) : 0;
   const caption = progress?.stage === "registry"
      ? (progress.total > 1
         ? Localization.getText("Fetching properties from the land registry…")
         : Localization.getText("Preparing easement review from land registry…"))
      : Localization.getText("Loading easements…");
   return (
      <div className="servitut-redegoerelse servitut-redegoerelse--boot">
         <div className="servitut-redegoerelse__boot" role="status" aria-live="polite">
            <Spinner animation="border" role="status" />
            <div className="servitut-redegoerelse__bootTitle">{caption}</div>
            <div className="servitut-redegoerelse__bootBar" role="progressbar">
               <div className="servitut-redegoerelse__bootTrack">
                  <div
                     className={`servitut-redegoerelse__bootFill${indeterminate ? " servitut-redegoerelse__bootFill--indeterminate" : ""}`}
                     style={indeterminate ? undefined : { width: `${percent}%` }}
                  />
               </div>
               {progress && progress.total > 0 ? (
                  <span className="servitut-redegoerelse__bootCount">{progress.completed}/{progress.total}</span>
               ) : null}
            </div>
         </div>
      </div>
   );
}

function ServitutRedegoerelseWindowBody(props: {
   managedWindow: ManagedWindow;
   prepareColorList?: SFEColorEntry[];
   datoLoebenrFilter?: string;
   canSeeA: boolean;
   canSeeB: boolean;
}) {
   const { state: sessionState } = useContext(SessionContext);
   const { dispatch: windowDispatch, state: windowState } = useWindowContext();
   const { dispatch: editorDispatch } = useANNEditorContext();
   const { state: annState, dispatch: annDispatch } = useANNContext();
   const saveAnnotation = useSaveAnnotation();
   const deleteAnnotation = useDeleteAnnotation();
   const { state, dispatch, getState } = useServitutReport();
   const session = {
      userId: sessionState?.userRef,
      userName: sessionState?.userName,
      organizationId: sessionState?.customerRef,
   };
   const recordsByIdRef = useRef(annState.recordsById);
   recordsByIdRef.current = annState.recordsById;
   const effects = useServitutReportEffects(dispatch, getState, session, {
      saveAnnotation,
      deleteAnnotation,
      annDispatch,
      getRecord: (id) => recordsByIdRef.current[id],
      listCachedRecords: () => Object.values(recordsByIdRef.current),
      confirmForeignDelete: (kind, savedByName) => confirmForeignAnnotationDelete(windowDispatch, kind, savedByName),
   });
   const [showFolderCompare, setShowFolderCompare] = useState(false);
   const stedfaestelseRowIdRef = useRef<string | null>(null);
   const editorWasOpenRef = useRef(isWindowOpen(windowState, WindowId.ANNEditor));
   const bConfirmDoneRef = useRef(false);
   const prepareKey = props.prepareColorList?.length
      ? `${props.prepareColorList.map((entry) => `${entry.sfe}:${entry.tingbogUuid || ""}`).join(",")}|${props.datoLoebenrFilter || ""}`
      : "";

   useEffect(() => {
      if (props.canSeeA) {
         dispatch({ type: ServitutReportActionType.SetPhase, payload: "A" });
      }
      clearStalePreparedCache();
      if (props.prepareColorList?.length) {
         void effects.prepare(props.prepareColorList, props.datoLoebenrFilter);
         return;
      }
      const skeleton = loadReportSkeleton();
      if (skeleton) {
         dispatch({ type: ServitutReportActionType.ReportPrepared, payload: skeleton });
         void effects.hydrateFromAnn({ bindPendingAkts: true });
      }
   }, [prepareKey, props.canSeeA, sessionState?.customerRef]);

   useEffect(() => {
      saveLastCaseDescription(state.caseDescription);
   }, [state.caseDescription]);

   useEffect(() => {
      if (state.status === "ready") {
         saveReportSkeleton(state);
      }
   }, [state.status, state.rowOrder, state.caseDescription, state.phase, state.columns]);

   useEffect(() => {
      if (state.status !== "ready") {
         return;
      }
      const records = Object.values(annState.recordsById);
      if (!records.length) {
         return;
      }
      dispatch({ type: ServitutReportActionType.AnnRecordsChanged, payload: { records } });
   }, [annState.recordsById, dispatch, state.status]);

   useEffect(() => {
      const editorOpen = isWindowOpen(windowState, WindowId.ANNEditor);
      const rowId = stedfaestelseRowIdRef.current;
      if (editorWasOpenRef.current && !editorOpen && rowId) {
         void effects.hydrateFromAnn();
      }
      editorWasOpenRef.current = editorOpen;
   }, [effects, windowState]);

   const canWriteA = props.canSeeA;
   const canWriteB = props.canSeeB;
   const incompleteARows = selectIncompleteARows(state);
   const rejectedARows = selectRejectedARows(state);

   function requestPhase(next: ServitutRedegoerelsePhase) {
      if (next === "B" && !props.canSeeB) {
         return;
      }
      if (next === "A" && !props.canSeeA) {
         return;
      }
      if (next === "B" && incompleteARows.length && !bConfirmDoneRef.current) {
         windowDispatch(openWindow(WindowId.ConfirmationDialog, {
            header: "Find/control is incomplete",
            children: [
               Localization.getFormattedText("{count} easements are not fetched or not controlled. Continue to analysis?", { count: incompleteARows.length }),
               rejectedARows.length
                  ? Localization.getFormattedText("{count} easements were ignored and are not being analysed.", { count: rejectedARows.length })
                  : "",
            ].filter(Boolean).join(" "),
            onSubmit: () => {
               bConfirmDoneRef.current = true;
               dispatch({ type: ServitutReportActionType.SetPhase, payload: "B" });
            },
         }));
         return;
      }
      dispatch({ type: ServitutReportActionType.SetPhase, payload: next });
   }

   function requestApprove(rowIds: string[]) {
      void effects.approvePages(rowIds);
   }

   async function openRow(rowId: string) {
      const row = selectRow(getState(), rowId);
      if (!row) {
         return;
      }
      dispatch({ type: ServitutReportActionType.SetOpeningRow, payload: rowId });
      try {
         let blob = await getRowSourcePdfBlob(row);
         if (!blob) {
            message.error(Localization.getText("No PDF available for this document"));
            return;
         }
         const inPhaseB = getState().phase === "B";
         if (inPhaseB) {
            blob = await withRowPaategningPages(await getCutPdfForContent(row) || blob, row);
         }
         const pdfUrl = URL.createObjectURL(blob);
         const pageCount = await getPdfPageCount(blob).catch(() => 0);
         const extracted = row.document.kind === "annotation" ? normalizePages(row.document.extractedAktPages) : [];
         const isCut = extracted.length > 0;
         const viewRow = {
            handling: row.landRegistry.handling,
            documentSource: row.document.kind === "upload" ? "customUpload" : row.document.kind === "annotation" ? "annotation" : "tingbog",
            selectedPages: row.pages.selected,
            analysisItem: row.analysis?.item,
         };
         const aktInitial = getViewerInitialAktPages(viewRow);
         const remappedSelected = isCut ? mapAktPagesToCutSelection(aktInitial, extracted) : aktInitial;
         const remappedAnalysis = isCut ? mapAktPagesToCutSelection(row.analysis?.item.pages_kept || [], extracted) : (row.analysis?.item.pages_kept || []);
         const openedPages = pageCount ? clampPagesToDocument(remappedSelected || aktInitial, pageCount) : (remappedSelected || aktInitial);
         const initialSelectedPages = inPhaseB && pageCount
            ? buildConsecutivePages(pageCount)
            : openedPages.length
               ? openedPages
               : (isDigitalRow(row) && pageCount ? buildConsecutivePages(pageCount) : openedPages);
         const documentId = rowDocumentId(row);
         const filename = selectWorkingDocumentLabel(row);
         const analysisItem = row.analysis?.item && isCut
            ? { ...row.analysis.item, pages_kept: remappedAnalysis ?? row.analysis.item.pages_kept ?? [] }
            : row.analysis?.item;
         const nativeDigital = isNativeDigitalDocument(row);
         const writeAktPages = (pages: number[]) => {
            dispatch({ type: ServitutReportActionType.SelectPages, payload: { rowId, aktPages: pages } });
         };
         windowDispatch(openWindow(WindowId.ANNPDFAnalyzerViewer, {
            pdfUrl,
            pdfBlob: blob,
            analysisResult: toViewerAnalysisResult(analysisItem ? { item: analysisItem, stale: Boolean(row.analysis?.stale) } : undefined, state.phase === "A" ? "summary" : "full"),
            analysisDisplayMode: state.phase === "A" ? "summary" : "full",
            contextType: AnnotationContextType.Servitut,
            organizationId: sessionState?.customerRef,
            userId: sessionState?.userRef,
            userName: sessionState?.userName,
            datoloebenr: row.identity.datoloebenr,
            documentHeading: row.landRegistry.summary,
            saveAndApprove: !nativeDigital && !inPhaseB,
            allowDocumentPersist: !nativeDigital && !inPhaseB,
            documentId,
            documentFilename: filename,
            aktId: selectRowAktId(row) || row.identity.aktId,
            initialSelectedPages,
            treatEmptyPagesKeptAsAll: isDigitalRow(row) || inPhaseB,
            pdfSourceKind: isCut ? "cutAnnotation" : "fullAkt",
            sourceExtractedPages: isCut ? extracted : undefined,
            paategningDokumentId: rowTingbogDokumentId(row) || undefined,
            paategningerIncluded: inPhaseB,
            onCloseSelectedPages: nativeDigital || inPhaseB ? undefined : writeAktPages,
            onDocumentSaved: nativeDigital || inPhaseB ? undefined : (pages: number[], savedRecord?: ANNAnnotationRecord) => {
               writeAktPages(pages);
               void effects.approvePages([rowId], savedRecord);
            },
         }));
      } catch (error) {
         message.error(error instanceof Error ? error.message : Localization.getText("Failed to open scanned akt PDF"));
      } finally {
         dispatch({ type: ServitutReportActionType.SetOpeningRow, payload: null });
      }
   }

   function openStedfaestelse(rowId: string) {
      const row = selectRow(getState(), rowId);
      if (!row) {
         return;
      }
      stedfaestelseRowIdRef.current = rowId;
      const identifier = canonicalDocumentId(row.landRegistry.servitut, row.id);
      const restoreMinimizedWindowIds = minimizeOpenWindowsForStedfaestelse(windowState, windowDispatch);
      editorDispatch({
         type: ANNEditorActionType.Open,
         payload: { contextType: AnnotationContextType.Servitut, identifier },
      });
      windowDispatch(openWindow(WindowId.ANNEditor, {
         contextType: AnnotationContextType.Servitut,
         identifier,
         datoloebenr: row.identity.datoloebenr,
         restoreMinimizedWindowIds,
      }));
   }

   return (
      <AdvancedDragModal
         window={props.managedWindow}
         title={Localization.getText("Easement review")}
         onClose={() => windowDispatch(closeWindow(props.managedWindow.id))}
         allowFullscreen={true}
         allowUserMiniMize={true}
         allowPopout={false}
         PosDefault={{ top: "48px", left: "48px", width: "1360px", height: "860px" }}
      >
         <ADModalBody>
            {state.status === "ready" ? (
               <ServitutMatrix
                  effects={effects}
                  canSeeA={props.canSeeA}
                  canSeeB={props.canSeeB}
                  canWriteA={canWriteA}
                  canWriteB={canWriteB}
                  onPhaseChange={requestPhase}
                  onOpenRow={(rowId) => { void openRow(rowId); }}
                  onOpenStedfaestelse={openStedfaestelse}
                  onApprovePages={requestApprove}
                  showFolderCompare={showFolderCompare}
                  onShowFolderCompare={setShowFolderCompare}
               />
            ) : (
               <BootProgress progress={state.loadProgress} error={state.loadError} />
            )}
         </ADModalBody>
      </AdvancedDragModal>
   );
}

export function ServitutRedegoerelseWindow(props: { managedWindow: ManagedWindow }) {
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const canSeeA = canUsePhaseA(hasAccessToFeature);
   const canSeeB = canUsePhaseB(hasAccessToFeature);
   const windowParams = props.managedWindow.windowParams as ServitutRedegoerelseWindowParams | undefined;
   const initialPhase: ServitutRedegoerelsePhase = canSeeA || !canSeeB ? "A" : "B";

   return (
      <ServitutReportProvider
         initialPhase={initialPhase}
         initialCaseDescription={loadLastCaseDescription()}
      >
         <ServitutRedegoerelseWindowBody
            managedWindow={props.managedWindow}
            prepareColorList={windowParams?.prepareColorList}
            datoLoebenrFilter={windowParams?.datoLoebenrFilter}
            canSeeA={canSeeA}
            canSeeB={canSeeB}
         />
      </ServitutReportProvider>
   );
}
